import { useEffect, useRef } from "react";

interface Props { theme: "dark" | "light"; lang: "fr" | "en" | "ar"; }

const content = {
  fr: {
    overline: "FONCTIONNALITÉS",
    h2: "Tout ce dont vous avez besoin",
    sub: "Une application conçue pour les conducteurs mauritaniens, avec des données en temps réel alimentées par la communauté.",
    features: [
      { icon: "ri-map-2-line", color: "#007AFF", bg: "rgba(0,122,255,0.10)", title: "Carte interactive", desc: "15 wilayas couvertes, carte temps réel avec votre position et toutes les stations autour de vous." },
      { icon: "ri-flashlight-line", color: "#34C759", bg: "rgba(52,199,89,0.10)", title: "Temps réel", desc: "Les conducteurs mettent à jour — vous voyez instantanément. Données fraîches à chaque seconde." },
      { icon: "ri-notification-3-line", color: "#FF9500", bg: "rgba(255,149,0,0.10)", title: "Alertes carburant", desc: "Notification quand du carburant est disponible près de vous. Ne manquez plus jamais une station." },
      { icon: "ri-gas-station-line", color: "#FF3B30", bg: "rgba(255,59,48,0.10)", title: "Diesel & Essence", desc: "Prix et statut (disponible / faible / épuisé) pour chaque type de carburant, par station." },
      { icon: "ri-medal-line", color: "#007AFF", bg: "rgba(0,122,255,0.10)", title: "Badges contributeur", desc: "Gagnez des badges. Les meilleurs contributeurs sont récompensés et mis en avant dans la communauté." },
      { icon: "ri-translate-2", color: "#34C759", bg: "rgba(52,199,89,0.10)", title: "Trilingue", desc: "Interface en Français, English, et العربية avec support RTL complet pour les utilisateurs arabophones." },
    ],
  },
  en: {
    overline: "FEATURES",
    h2: "Everything you need",
    sub: "An app built for Mauritanian drivers, with real-time community-powered data.",
    features: [
      { icon: "ri-map-2-line", color: "#007AFF", bg: "rgba(0,122,255,0.10)", title: "Interactive map", desc: "15 wilayas covered, real-time map with your location and all stations around you." },
      { icon: "ri-flashlight-line", color: "#34C759", bg: "rgba(52,199,89,0.10)", title: "Real-time", desc: "Drivers update — you see instantly. Fresh data every second, powered by the community." },
      { icon: "ri-notification-3-line", color: "#FF9500", bg: "rgba(255,149,0,0.10)", title: "Fuel alerts", desc: "Notification when fuel is available near you. Never miss a station again." },
      { icon: "ri-gas-station-line", color: "#FF3B30", bg: "rgba(255,59,48,0.10)", title: "Diesel & Gasoline", desc: "Price and status (available / low / out) for each fuel type, per station." },
      { icon: "ri-medal-line", color: "#007AFF", bg: "rgba(0,122,255,0.10)", title: "Contributor badges", desc: "Earn badges. Top contributors are rewarded and featured in the community." },
      { icon: "ri-translate-2", color: "#34C759", bg: "rgba(52,199,89,0.10)", title: "Trilingual", desc: "Interface in Français, English, and العربية with full RTL support for Arabic users." },
    ],
  },
  ar: {
    overline: "المميزات",
    h2: "كل ما تحتاجه",
    sub: "تطبيق مصمم للسائقين الموريتانيين، مع بيانات فورية مدعومة من المجتمع.",
    features: [
      { icon: "ri-map-2-line", color: "#007AFF", bg: "rgba(0,122,255,0.10)", title: "خريطة تفاعلية", desc: "15 ولاية مغطاة، خريطة فورية مع موقعك وجميع المحطات حولك." },
      { icon: "ri-flashlight-line", color: "#34C759", bg: "rgba(52,199,89,0.10)", title: "وقت فعلي", desc: "السائقون يحدّثون — أنت ترى فوراً. بيانات حديثة في كل ثانية." },
      { icon: "ri-notification-3-line", color: "#FF9500", bg: "rgba(255,149,0,0.10)", title: "تنبيهات الوقود", desc: "إشعار عند توفر الوقود بالقرب منك. لا تفوت أي محطة." },
      { icon: "ri-gas-station-line", color: "#FF3B30", bg: "rgba(255,59,48,0.10)", title: "ديزل وبنزين", desc: "السعر والحالة (متاح / منخفض / نفد) لكل نوع وقود، لكل محطة." },
      { icon: "ri-medal-line", color: "#007AFF", bg: "rgba(0,122,255,0.10)", title: "شارات المساهمين", desc: "اكسب شارات. أفضل المساهمين يُكافأون ويُبرزون في المجتمع." },
      { icon: "ri-translate-2", color: "#34C759", bg: "rgba(52,199,89,0.10)", title: "ثلاثي اللغات", desc: "واجهة بالفرنسية والإنجليزية والعربية مع دعم كامل للكتابة من اليمين لليسار." },
    ],
  },
};

export default function FeaturesSection({ theme, lang }: Props) {
  const isDark = theme === "dark";
  const t = content[lang];
  const isRtl = lang === "ar";
  const sectionRef = useRef<HTMLDivElement>(null);
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textMuted = isDark ? "text-white/60" : "text-gray-500";
  const cardBg = isDark ? "bg-[rgba(28,28,30,0.85)] border-white/10" : "bg-white/85 border-black/10";

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.querySelectorAll<HTMLElement>(".feature-card").forEach((card, i) => {
            setTimeout(() => { card.style.opacity = "1"; card.style.transform = "translateY(0)"; }, i * 80);
          });
        }
      });
    }, { threshold: 0.1 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" className="py-28 relative" style={{ background: isDark ? "#000" : "#F2F2F7" }} dir={isRtl ? "rtl" : "ltr"}>
      <div className={`absolute top-0 left-0 right-0 h-px ${isDark ? "bg-white/5" : "bg-black/5"}`} />
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" ref={sectionRef}>
        <div className="text-center mb-16">
          <p className="text-[#007AFF] text-xs font-semibold tracking-[0.15em] uppercase mb-4">{t.overline}</p>
          <h2 className={`text-4xl md:text-5xl font-bold tracking-tight mb-5 ${textPrimary}`} style={{ fontFamily: "-apple-system,'SF Pro Display',system-ui,sans-serif" }}>{t.h2}</h2>
          <p className={`text-lg max-w-[520px] mx-auto leading-relaxed ${textMuted}`}>{t.sub}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {t.features.map((f, i) => (
            <div key={i} className={`feature-card p-7 rounded-2xl backdrop-blur-[20px] border transition-all duration-300 hover:-translate-y-1.5 hover:border-[#007AFF]/30 cursor-default ${cardBg}`}
              style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 0.5s ease, transform 0.5s ease, border-color 0.3s ease" }}>
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl mb-5" style={{ background: f.bg }}>
                <i className={`${f.icon} text-2xl`} style={{ color: f.color }} />
              </div>
              <h3 className={`text-lg font-semibold mb-3 ${textPrimary}`} style={{ fontFamily: "-apple-system,'SF Pro Display',system-ui,sans-serif" }}>{f.title}</h3>
              <p className={`text-sm leading-relaxed ${textMuted}`}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
